import React, { useCallback, useEffect, useMemo, useState } from "react";
import "chart.js/auto";
import Select from "react-select";

import ChartCard from "../common/charts/ChartCard";
import BarChart from "../common/charts/BarChart";
import DoughnutChart from "../common/charts/DoughnutChart";
import PieChart from "../common/charts/PieChart";
import PolarAreaChart from "../common/charts/PolarAreaChart";
import TooltipText from "../common/tooltipText";

const BatchDashboardView = ({
  stats,
  overall,
  subjectWiseStats,
  studentHierarchyCompletion,
  teacherHierarchyCompletion,
  toMinutes,
  showOverview = true,
  showLeaderboards = true,
}) => {
  const safeStats = stats || {};
  const safeOverall = overall || {};

  const getSubjectKey = useCallback(
    (s) =>
      String(
        s?.subject_key ??
          s?.subjectKey ??
          s?.subject_id ??
          s?.subjectId ??
          s?.id ??
          "",
      ),
    [],
  );
  const getTopicKey = useCallback(
    (t) =>
      String(
        t?.topic_key ?? t?.topicKey ?? t?.topic_id ?? t?.topicId ?? t?.id ?? "",
      ),
    [],
  );
  const getSubtopicKey = useCallback(
    (st) =>
      String(
        st?.subtopic_key ??
          st?.subtopicKey ??
          st?.subtopic_id ??
          st?.subtopicId ??
          st?.id ??
          "",
      ),
    [],
  );

  const subjectWiseStatsArr = Array.isArray(subjectWiseStats)
    ? subjectWiseStats
    : Array.isArray(safeStats?.subject_wise_stats)
      ? safeStats.subject_wise_stats
      : [];

  const studentHierarchyArr = useMemo(() => {
    if (Array.isArray(studentHierarchyCompletion))
      return studentHierarchyCompletion;
    if (Array.isArray(safeStats?.student_hierarchy_completion))
      return safeStats.student_hierarchy_completion;
    return [];
  }, [safeStats, studentHierarchyCompletion]);

  const teacherHierarchyArr = useMemo(() => {
    if (Array.isArray(teacherHierarchyCompletion))
      return teacherHierarchyCompletion;
    if (Array.isArray(safeStats?.teacher_hierarchy_completion))
      return safeStats.teacher_hierarchy_completion;
    return [];
  }, [safeStats, teacherHierarchyCompletion]);

  const courseCompletionChart = useMemo(() => {
    const overallStudent = Number(
      safeOverall?.avg_student_completion_percentage,
    );
    const overallTeacher = Number(
      safeOverall?.teacher_course_completion_percentage,
    );

    const hasOverallStudent = Number.isFinite(overallStudent);
    const hasOverallTeacher = Number.isFinite(overallTeacher);

    const hierarchyStudent = (
      Array.isArray(studentHierarchyArr) ? studentHierarchyArr : []
    )
      .map((s) => Number(s?.avg_completion_percentage))
      .filter((v) => Number.isFinite(v));
    const hierarchyTeacher = (
      Array.isArray(teacherHierarchyArr) ? teacherHierarchyArr : []
    )
      .map((s) => Number(s?.teacher_completion_percentage))
      .filter((v) => Number.isFinite(v));

    const studentAvg = hasOverallStudent
      ? overallStudent
      : hierarchyStudent.length
        ? hierarchyStudent.reduce((a, b) => a + b, 0) / hierarchyStudent.length
        : 0;
    const teacherAvg = hasOverallTeacher
      ? overallTeacher
      : hierarchyTeacher.length
        ? hierarchyTeacher.reduce((a, b) => a + b, 0) / hierarchyTeacher.length
        : 0;

    return {
      labels: ["Course"],
      studentVals: [Math.round(studentAvg * 100) / 100],
      teacherVals: [Math.round(teacherAvg * 100) / 100],
    };
  }, [safeOverall, studentHierarchyArr, teacherHierarchyArr]);

  const hierarchySubjectsArr = useMemo(() => {
    const byKey = new Map();

    (Array.isArray(studentHierarchyArr) ? studentHierarchyArr : []).forEach(
      (s) => {
        const k = getSubjectKey(s);
        if (!k) return;
        byKey.set(k, s);
      },
    );

    (Array.isArray(teacherHierarchyArr) ? teacherHierarchyArr : []).forEach(
      (s) => {
        const k = getSubjectKey(s);
        if (!k) return;
        if (!byKey.has(k)) byKey.set(k, s);
      },
    );

    return Array.from(byKey.values());
  }, [getSubjectKey, studentHierarchyArr, teacherHierarchyArr]);

  const subjectOptions = useMemo(() => {
    const source = hierarchySubjectsArr;
    return (Array.isArray(source) ? source : [])
      .map((s) => ({
        value: getSubjectKey(s),
        label: s?.subject_name || s?.subject_key || s?.subjectKey || "Subject",
      }))
      .filter((o) => o.value);
  }, [getSubjectKey, hierarchySubjectsArr]);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);

  useEffect(() => {
    setSelectedSubject(subjectOptions?.[0] || null);
    setSelectedTopic(null);
    setSelectedSubtopic(null);
  }, [subjectOptions]);

  useEffect(() => {
    if (selectedSubject) return;
    if (!Array.isArray(subjectOptions) || subjectOptions.length === 0) return;
    setSelectedSubject(subjectOptions[0]);
  }, [selectedSubject, subjectOptions]);

  const studentSelectedSubjectHierarchyObj = useMemo(() => {
    const key = selectedSubject?.value ? String(selectedSubject.value) : "";
    if (!key) return null;
    return studentHierarchyArr.find((s) => getSubjectKey(s) === key) || null;
  }, [getSubjectKey, selectedSubject, studentHierarchyArr]);

  const teacherSelectedSubjectHierarchyObj = useMemo(() => {
    const key = selectedSubject?.value ? String(selectedSubject.value) : "";
    if (!key) return null;
    return teacherHierarchyArr.find((s) => getSubjectKey(s) === key) || null;
  }, [getSubjectKey, selectedSubject, teacherHierarchyArr]);

  const topicsForSelectedSubject = useMemo(() => {
    const studentTopics = Array.isArray(
      studentSelectedSubjectHierarchyObj?.topics,
    )
      ? studentSelectedSubjectHierarchyObj.topics
      : [];
    const teacherTopics = Array.isArray(
      teacherSelectedSubjectHierarchyObj?.topics,
    )
      ? teacherSelectedSubjectHierarchyObj.topics
      : [];

    const byKey = new Map();
    studentTopics.forEach((t) => {
      const k = getTopicKey(t);
      if (!k) return;
      byKey.set(k, t);
    });
    teacherTopics.forEach((t) => {
      const k = getTopicKey(t);
      if (!k) return;
      if (!byKey.has(k)) byKey.set(k, t);
    });

    return Array.from(byKey.values());
  }, [
    getTopicKey,
    studentSelectedSubjectHierarchyObj,
    teacherSelectedSubjectHierarchyObj,
  ]);

  const topicOptions = useMemo(
    () =>
      topicsForSelectedSubject
        .map((t) => ({
          value: getTopicKey(t),
          label: t?.topic_name || t?.topic_key || t?.topicKey || "Topic",
        }))
        .filter((o) => o.value),
    [topicsForSelectedSubject],
  );

  const studentSelectedTopicHierarchyObj = useMemo(() => {
    const key = selectedTopic?.value ? String(selectedTopic.value) : "";
    if (!key) return null;
    const topics = Array.isArray(studentSelectedSubjectHierarchyObj?.topics)
      ? studentSelectedSubjectHierarchyObj.topics
      : [];
    return topics.find((t) => getTopicKey(t) === key) || null;
  }, [getTopicKey, selectedTopic, studentSelectedSubjectHierarchyObj]);

  const teacherSelectedTopicHierarchyObj = useMemo(() => {
    const key = selectedTopic?.value ? String(selectedTopic.value) : "";
    if (!key) return null;
    const topics = Array.isArray(teacherSelectedSubjectHierarchyObj?.topics)
      ? teacherSelectedSubjectHierarchyObj.topics
      : [];
    return topics.find((t) => getTopicKey(t) === key) || null;
  }, [getTopicKey, selectedTopic, teacherSelectedSubjectHierarchyObj]);

  const subtopicsForSelectedTopic = useMemo(() => {
    const studentSubtopics = Array.isArray(
      studentSelectedTopicHierarchyObj?.subtopics,
    )
      ? studentSelectedTopicHierarchyObj.subtopics
      : [];
    const teacherSubtopics = Array.isArray(
      teacherSelectedTopicHierarchyObj?.subtopics,
    )
      ? teacherSelectedTopicHierarchyObj.subtopics
      : [];

    const byKey = new Map();
    studentSubtopics.forEach((st) => {
      const k = getSubtopicKey(st);
      if (!k) return;
      byKey.set(k, st);
    });
    teacherSubtopics.forEach((st) => {
      const k = getSubtopicKey(st);
      if (!k) return;
      if (!byKey.has(k)) byKey.set(k, st);
    });

    return Array.from(byKey.values());
  }, [
    getSubtopicKey,
    studentSelectedTopicHierarchyObj,
    teacherSelectedTopicHierarchyObj,
  ]);

  const subtopicOptions = useMemo(
    () =>
      subtopicsForSelectedTopic
        .map((st) => ({
          value: getSubtopicKey(st),
          label:
            st?.subtopic_name ||
            st?.subtopic_key ||
            st?.subtopicKey ||
            "Subtopic",
        }))
        .filter((o) => o.value),
    [subtopicsForSelectedTopic],
  );

  const selectedSubtopicObj = useMemo(() => {
    const key = selectedSubtopic?.value ? String(selectedSubtopic.value) : "";
    if (!key) return null;
    return (
      subtopicsForSelectedTopic.find((st) => getSubtopicKey(st) === key) || null
    );
  }, [selectedSubtopic, subtopicsForSelectedTopic]);

  const selectedSubjectStatsObj = useMemo(() => {
    const key = selectedSubject?.value ? String(selectedSubject.value) : "";
    if (!key) return null;
    return subjectWiseStatsArr.find((s) => getSubjectKey(s) === key) || null;
  }, [getSubjectKey, selectedSubject, subjectWiseStatsArr]);

  const selectedTopicStatsObj = useMemo(() => {
    const key = selectedTopic?.value ? String(selectedTopic.value) : "";
    if (!key || !selectedSubjectStatsObj) return null;
    const topics = Array.isArray(selectedSubjectStatsObj?.topics)
      ? selectedSubjectStatsObj.topics
      : [];
    return topics.find((t) => getTopicKey(t) === key) || null;
  }, [getTopicKey, selectedSubjectStatsObj, selectedTopic]);

  const selectedSubtopicStatsObj = useMemo(() => {
    const key = selectedSubtopic?.value ? String(selectedSubtopic.value) : "";
    if (!key || !selectedTopicStatsObj) return null;
    const subtopics = Array.isArray(selectedTopicStatsObj?.subtopics)
      ? selectedTopicStatsObj.subtopics
      : [];
    return subtopics.find((st) => getSubtopicKey(st) === key) || null;
  }, [getSubtopicKey, selectedSubtopic, selectedTopicStatsObj]);

  const hierarchyCompletionChart = useMemo(() => {
    const hasStudentHierarchy =
      Array.isArray(studentHierarchyArr) && studentHierarchyArr.length > 0;
    const hasTeacherHierarchy =
      Array.isArray(teacherHierarchyArr) && teacherHierarchyArr.length > 0;
    if (!hasStudentHierarchy && !hasTeacherHierarchy) return null;

    const teacherSubjectsByKey = new Map();
    teacherHierarchyArr.forEach((s) => {
      const k = getSubjectKey(s);
      if (k) teacherSubjectsByKey.set(k, s);
    });

    const subjectKey = selectedSubject?.value
      ? String(selectedSubject.value)
      : "";
    const topicKey = selectedTopic?.value ? String(selectedTopic.value) : "";
    const subtopicKey = selectedSubtopic?.value
      ? String(selectedSubtopic.value)
      : "";

    const studentSubject = subjectKey
      ? studentHierarchyArr.find((s) => getSubjectKey(s) === subjectKey) || null
      : null;
    const teacherSubject = subjectKey
      ? teacherSubjectsByKey.get(subjectKey) || null
      : null;

    if (!subjectKey) {
      const labels = studentHierarchyArr.map(
        (s) => s?.subject_name || s?.subject_key || "Subject",
      );
      const studentVals = studentHierarchyArr.map(
        (s) => Number(s?.avg_completion_percentage) || 0,
      );
      const teacherVals = studentHierarchyArr.map((s) => {
        const t = teacherSubjectsByKey.get(getSubjectKey(s));
        return Number(t?.teacher_completion_percentage) || 0;
      });
      return { labels, studentVals, teacherVals };
    }

    if (!topicKey) {
      const studentTopics = Array.isArray(studentSubject?.topics)
        ? studentSubject.topics
        : [];
      const teacherTopics = Array.isArray(teacherSubject?.topics)
        ? teacherSubject.topics
        : [];

      const labels = studentTopics.map(
        (t) => t?.topic_name || t?.topic_key || "Topic",
      );
      const studentVals = studentTopics.map(
        (t) => Number(t?.avg_completion_percentage) || 0,
      );
      const teacherVals = studentTopics.map((t) => {
        const tt = teacherTopics.find((x) => getTopicKey(x) === getTopicKey(t));
        return Number(tt?.teacher_completion_percentage) || 0;
      });
      return { labels, studentVals, teacherVals };
    }

    const studentTopic = Array.isArray(studentSubject?.topics)
      ? studentSubject.topics.find((t) => getTopicKey(t) === topicKey) || null
      : null;
    const teacherTopic = Array.isArray(teacherSubject?.topics)
      ? teacherSubject.topics.find((t) => getTopicKey(t) === topicKey) || null
      : null;

    if (!subtopicKey) {
      const studentSubtopics = Array.isArray(studentTopic?.subtopics)
        ? studentTopic.subtopics
        : [];
      const teacherSubtopics = Array.isArray(teacherTopic?.subtopics)
        ? teacherTopic.subtopics
        : [];

      const labels = studentSubtopics.map(
        (st) => st?.subtopic_name || st?.subtopic_key || "Subtopic",
      );
      const studentVals = studentSubtopics.map(
        (st) => Number(st?.avg_completion_percentage) || 0,
      );
      const teacherVals = studentSubtopics.map((st) => {
        const tst = teacherSubtopics.find(
          (x) => getSubtopicKey(x) === getSubtopicKey(st),
        );
        return Number(tst?.teacher_completion_percentage) || 0;
      });
      return { labels, studentVals, teacherVals };
    }

    const studentSubtopic = Array.isArray(studentTopic?.subtopics)
      ? studentTopic.subtopics.find(
          (st) => getSubtopicKey(st) === subtopicKey,
        ) || null
      : null;
    const teacherSubtopic = Array.isArray(teacherTopic?.subtopics)
      ? teacherTopic.subtopics.find(
          (st) => getSubtopicKey(st) === subtopicKey,
        ) || null
      : null;

    return {
      labels: [
        studentSubtopic?.subtopic_name ||
          studentSubtopic?.subtopic_key ||
          "Subtopic",
      ],
      studentVals: [Number(studentSubtopic?.avg_completion_percentage) || 0],
      teacherVals: [
        Number(teacherSubtopic?.teacher_completion_percentage) || 0,
      ],
    };
  }, [
    getSubjectKey,
    getSubtopicKey,
    getTopicKey,
    selectedSubject,
    selectedSubtopic,
    selectedTopic,
    studentHierarchyArr,
    teacherHierarchyArr,
  ]);

  const distributionLevel = useMemo(() => {
    if (selectedSubtopic?.value) return "subtopic";
    if (selectedTopic?.value) return "topic";
    if (selectedSubject?.value) return "subject";
    return "all_subjects";
  }, [selectedSubject, selectedSubtopic, selectedTopic]);

  const activeDistributionSource = useMemo(() => {
    if (selectedSubtopicStatsObj) return [selectedSubtopicStatsObj];
    if (selectedTopicStatsObj) return [selectedTopicStatsObj];
    if (selectedSubjectStatsObj) return [selectedSubjectStatsObj];
    return subjectWiseStatsArr;
  }, [
    selectedSubjectStatsObj,
    selectedSubtopicStatsObj,
    selectedTopicStatsObj,
    subjectWiseStatsArr,
  ]);

  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
      accentsHover: [
        "rgba(252, 164, 59, 0.95)",
        "rgba(133, 238, 173, 0.95)",
        "rgba(212, 249, 45, 0.95)",
        "rgba(250, 238, 112, 0.95)",
        "rgba(148, 163, 184, 0.85)",
      ],
    }),
    [],
  );

  const sliceColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accents[i % chartPalette.accents.length],
    );

  const sliceHoverColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accentsHover[i % chartPalette.accentsHover.length],
    );

  const buildDistribution = (key) => {
    const totals = {
      above_70: 0,
      above_50: 0,
      below_50: 0,
      not_attended: 0,
    };

    activeDistributionSource.forEach((s) => {
      const dist = s && typeof s === "object" ? s[key] : null;
      if (!dist || typeof dist !== "object") return;
      totals.above_70 += Number(dist.above_70) || 0;
      totals.above_50 += Number(dist.above_50) || 0;
      totals.below_50 += Number(dist.below_50) || 0;
      totals.not_attended += Number(dist.not_attended) || 0;
    });

    const sum =
      totals.above_70 + totals.above_50 + totals.below_50 + totals.not_attended;

    if (!sum) {
      return {
        labels: ["No data"],
        datasets: [
          {
            data: [1],
            backgroundColor: [chartPalette.solid.slate],
            hoverBackgroundColor: [chartPalette.hover.slate],
            borderColor: "rgba(255, 255, 255, 0.9)",
            borderWidth: 1,
          },
        ],
      };
    }

    return {
      labels: ["Above 70", "Above 50", "Below 50", "Not Attended"],
      datasets: [
        {
          data: [
            totals.above_70,
            totals.above_50,
            totals.below_50,
            totals.not_attended,
          ],
          backgroundColor: sliceColors(4),
          hoverBackgroundColor: sliceHoverColors(4),
          borderColor: "rgba(255, 255, 255, 0.9)",
          borderWidth: 1,
        },
      ],
    };
  };

  const pagesCompletedLeaderboard = Array.isArray(
    safeStats?.pages_completed?.leaderboard,
  )
    ? safeStats.pages_completed.leaderboard
    : [];

  const homeworkLeaderboard = Array.isArray(safeStats?.homework?.leaderboard)
    ? safeStats.homework.leaderboard
    : [];

  const activeRecallLeaderboard = Array.isArray(
    safeStats?.active_recall?.leaderboard,
  )
    ? safeStats.active_recall.leaderboard
    : [];

  const quizzesLeaderboard = Array.isArray(safeStats?.quizzes?.leaderboard)
    ? safeStats.quizzes.leaderboard
    : [];

  const testsLeaderboard = Array.isArray(safeStats?.tests?.leaderboard)
    ? safeStats.tests.leaderboard
    : [];

  const adaptiveLearningLeaderboard = Array.isArray(
    safeStats?.adaptive_learning?.leaderboard,
  )
    ? safeStats.adaptive_learning.leaderboard
    : [];

  const timeSpentLeaderboard = Array.isArray(safeStats?.time_spent?.leaderboard)
    ? safeStats.time_spent.leaderboard
    : [];

  const coinsLeaderboard = Array.isArray(safeStats?.coins?.leaderboard)
    ? safeStats.coins.leaderboard
    : [];

  const xpLeaderboard = Array.isArray(safeStats?.xp?.leaderboard)
    ? safeStats.xp.leaderboard
    : [];

  const totalStudents = safeOverall.total_students ?? 0;

  const progressCards = [
    {
      key: "homework",
      title: "Homework",
      total: safeOverall.total_homework_assigned ?? 0,
      assigned: safeOverall.total_homework_assigned ?? 0,
      completed: safeOverall.total_homework_completed ?? 0,
      color: chartPalette.solid.mint,
    },
    {
      key: "quiz",
      title: "Quiz",
      total:
        safeOverall.total_quiz_pages ?? safeOverall.total_quiz_assigned ?? 0,
      assigned: safeOverall.total_quiz_assigned ?? 0,
      completed: safeOverall.total_quiz_fully_completed ?? 0,
      color: chartPalette.solid.lime,
    },
    {
      key: "test",
      title: "Test",
      total:
        safeOverall.total_test_pages ?? safeOverall.total_test_assigned ?? 0,
      assigned: safeOverall.total_test_assigned ?? 0,
      completed: safeOverall.total_test_fully_completed ?? 0,
      color: chartPalette.solid.orange,
    },
    {
      key: "subject_pages",
      title: "Subject Pages",
      total: safeOverall.total_subject_pages ?? 0,
      assigned: safeOverall.total_subject_pages ?? 0,
      completed: safeOverall.total_subject_pages_completed ?? 0,
      color: chartPalette.solid.yellow,
    },
  ];

  const cards = [
    { label: "Total Students", value: totalStudents },
    {
      label: "Total Homework Assigned",
      value: safeOverall.total_homework_assigned ?? 0,
    },
    {
      label: "Total Quizzes Assigned",
      value: safeOverall.total_quiz_assigned ?? 0,
    },
    {
      label: "Total Tests Assigned",
      value: safeOverall.total_test_assigned ?? 0,
    },
  ].filter((c) => c.value !== "");

  return (
    <section className="dashboard-course-section">
      {showOverview ? (
        <section className="dashboard-overview-section">
          <h2>Overview</h2>
          <div className="overview-grid">
            {cards.map((c) => (
              <div key={c.label} className="card overview-card">
                <div className="overview-card-content">
                  <div className="overview-card-text">
                    <TooltipText
                      text={c.label}
                      className="overview-card-label"
                      fullWidth
                    />
                    <strong>{c.value}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* <div className="overview-progress-cards">
            {progressCards.map((p) => {
              const total = Number(p.total) || 0;
              const assignedRaw = Number(p.assigned) || 0;
              const assigned = total > 0 ? Math.min(assignedRaw, total) : 0;
              const remaining = Math.max(0, total - assigned);
              const pct = total > 0 ? Math.round((assigned / total) * 100) : 0;
              const completedRaw = Number(p.completed) || 0;

              const centerTextPlugin = {
                id: `centerText_${p.key}`,
                afterDraw: (chart) => {
                  const ctx = chart?.ctx;
                  const meta = chart?.getDatasetMeta
                    ? chart.getDatasetMeta(0)
                    : null;
                  const arc = meta?.data && meta.data[0];
                  if (!ctx || !arc) return;

                  const { x, y } = arc;
                  ctx.save();
                  ctx.fillStyle = "rgba(226, 232, 240, 1)";
                  ctx.textAlign = "center";
                  ctx.textBaseline = "middle";
                  ctx.font =
                    "600 14px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial";
                  ctx.fillText(`${Math.max(0, Math.min(100, pct))}%`, x, y);
                  ctx.restore();
                },
              };
              return (
                <div key={p.key} className="card overview-progress-card">
                  <div className="overview-progress-content">
                    <div className="overview-progress-donut">
                      <DoughnutChart
                        data={{
                          labels: ["Assigned", "Remaining"],
                          datasets: [
                            {
                              data: [assigned, remaining],
                              backgroundColor: [
                                p.color,
                                chartPalette.soft.slate,
                              ],
                              hoverBackgroundColor: [
                                p.color,
                                chartPalette.soft.slate,
                              ],
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          cutout: "72%",
                          plugins: {
                            legend: { display: false },
                            tooltip: { enabled: false },
                          },
                        }}
                        plugins={[centerTextPlugin]}
                      />
                    </div>

                    <div className="overview-progress-text">
                      <div className="overview-progress-title">{p.title}</div>
                      <div className="overview-progress-rows">
                        <div className="overview-progress-row">
                          <span className="overview-progress-label">Total</span>
                          <span className="overview-progress-value">
                            {Number(p.total) || 0}
                          </span>
                        </div>
                        <div className="overview-progress-row">
                          <span className="overview-progress-label">
                            Assigned
                          </span>
                          <span className="overview-progress-value">
                            {assignedRaw}
                          </span>
                        </div>
                        <div className="overview-progress-row">
                          <span className="overview-progress-label">
                            Completed
                          </span>
                          <span className="overview-progress-value">
                            {completedRaw}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div> */}
        </section>
      ) : null}

      {Array.isArray(subjectOptions) && subjectOptions.length > 0 ? (
        <>
          <div className="course-select-row">
            <label>Subject</label>
            <div className="course-select-wrapper">
              <Select
                value={selectedSubject}
                onChange={(v) => {
                  setSelectedSubject(v);
                  setSelectedTopic(null);
                  setSelectedSubtopic(null);
                }}
                options={subjectOptions}
                isClearable
                placeholder="Select subject"
              />
            </div>

            <label>Topic</label>
            <div className="course-select-wrapper">
              <Select
                value={selectedTopic}
                onChange={(v) => {
                  setSelectedTopic(v);
                  setSelectedSubtopic(null);
                }}
                options={topicOptions}
                isClearable
                isDisabled={!selectedSubject}
                placeholder="Select topic"
              />
            </div>

            <label>Subtopic</label>
            <div className="course-select-wrapper">
              <Select
                value={selectedSubtopic}
                onChange={(v) => setSelectedSubtopic(v)}
                options={subtopicOptions}
                isClearable
                isDisabled={!selectedTopic}
                placeholder="Select subtopic"
              />
            </div>
          </div>

          {hierarchyCompletionChart ? (
            <div className="dashboard-course-grid">
              <ChartCard title="Completion (%)">
                <BarChart
                  data={{
                    labels: hierarchyCompletionChart.labels,
                    datasets: [
                      {
                        label: "Student Avg (%)",
                        data: hierarchyCompletionChart.studentVals,
                        backgroundColor: chartPalette.solid.mint,
                        hoverBackgroundColor: chartPalette.hover.mint,
                        borderRadius: 10,
                      },
                      {
                        label: "Teacher (%)",
                        data: hierarchyCompletionChart.teacherVals,
                        backgroundColor: chartPalette.solid.orange,
                        hoverBackgroundColor: chartPalette.hover.orange,
                        borderRadius: 10,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: true, position: "bottom" } },
                    scales: { y: { beginAtZero: true, max: 100 } },
                  }}
                />
              </ChartCard>
            </div>
          ) : null}

          <h2>
            {distributionLevel === "subtopic"
              ? "Subtopic-wise Distributions"
              : distributionLevel === "topic"
                ? "Topic-wise Distributions"
                : "Subject-wise Distributions"}
          </h2>
          <div className="dashboard-course-grid">
            <ChartCard title="Test Score Distribution">
              <PieChart
                data={buildDistribution("test_score_distribution")}
                options={{ responsive: true }}
              />
            </ChartCard>

            <ChartCard title="Quiz Score Distribution">
              <PieChart
                data={buildDistribution("quiz_score_distribution")}
                options={{ responsive: true }}
              />
            </ChartCard>

            <ChartCard title="Adaptive Score Distribution">
              <PieChart
                data={buildDistribution("adaptive_score_distribution")}
                options={{ responsive: true }}
              />
            </ChartCard>

            <ChartCard title="Active Recall Score Distribution">
              <PieChart
                data={buildDistribution("active_recall_score_distribution")}
                options={{ responsive: true }}
              />
            </ChartCard>
          </div>
        </>
      ) : null}

      {showLeaderboards ? (
        <>
          <h2>Leaderboards</h2>
          <div className="dashboard-course-grid">
            <ChartCard title="Subject Pages Completed">
              <BarChart
                data={{
                  labels: pagesCompletedLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Subject Pages Completed",
                      data: pagesCompletedLeaderboard.map(
                        (s) => Number(s.total_subject_pages_completed) || 0,
                      ),
                      backgroundColor: chartPalette.solid.yellow,
                      hoverBackgroundColor: chartPalette.hover.yellow,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>

            <ChartCard title="Active Recall Completed">
              <PolarAreaChart
                data={{
                  labels: activeRecallLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Active Recall Completed",
                      data: activeRecallLeaderboard.map(
                        (s) => Number(s.total_active_recall_completed) || 0,
                      ),
                      backgroundColor: sliceColors(
                        activeRecallLeaderboard.length,
                      ),
                      hoverBackgroundColor: sliceHoverColors(
                        activeRecallLeaderboard.length,
                      ),
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                }}
              />
            </ChartCard>

            <ChartCard title="Homework Completed">
              <BarChart
                data={{
                  labels: homeworkLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Homework Completed",
                      data: homeworkLeaderboard.map(
                        (s) => Number(s.total_homework_completed) || 0,
                      ),
                      backgroundColor: chartPalette.solid.mint,
                      hoverBackgroundColor: chartPalette.hover.mint,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>

            <ChartCard title="Quiz Completion Rate (%)">
              <BarChart
                data={{
                  labels: quizzesLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Quiz Completion (%)",
                      data: quizzesLeaderboard.map(
                        (s) =>
                          Number(
                            s.quiz_completion_rate ??
                              s.quiz_completion_percentage ??
                              s.quizzes_completion_rate ??
                              s.total_quiz_completed ??
                              s.total_quiz_pages_completed ??
                              s.total_quiz_fully_completed ??
                              0,
                          ) || 0,
                      ),
                      backgroundColor: chartPalette.solid.lime,
                      hoverBackgroundColor: chartPalette.hover.lime,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { beginAtZero: true, max: 100 } },
                }}
              />
            </ChartCard>

            <ChartCard title="Test Completion Rate (%)">
              <BarChart
                data={{
                  labels: testsLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Test Completion (%)",
                      data: testsLeaderboard.map(
                        (s) => Number(s.test_completion_rate) || 0,
                      ),
                      backgroundColor: chartPalette.solid.orange,
                      hoverBackgroundColor: chartPalette.hover.orange,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { beginAtZero: true, max: 100 } },
                }}
              />
            </ChartCard>

            <ChartCard title="Adaptive Learning Completed">
              <BarChart
                data={{
                  labels: adaptiveLearningLeaderboard.map(
                    (s) => s.student_name,
                  ),
                  datasets: [
                    {
                      label: "Adaptive Learning Completed",
                      data: adaptiveLearningLeaderboard.map(
                        (s) =>
                          Number(
                            s.total_adaptive_tests_completed ??
                              s.total_adaptive_learning_completed ??
                              s.adaptive_tests_completed ??
                              s.adaptive_learning_completed ??
                              s.adaptive_completed ??
                              0,
                          ) || 0,
                      ),
                      backgroundColor: chartPalette.solid.mint,
                      hoverBackgroundColor: chartPalette.hover.mint,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>

            <ChartCard title="Coins Earned">
              <PieChart
                data={{
                  labels: coinsLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Coins Earned",
                      data: coinsLeaderboard.map(
                        (s) => Number(s.total_coins) || 0,
                      ),
                      backgroundColor: sliceColors(coinsLeaderboard.length),
                      hoverBackgroundColor: sliceHoverColors(
                        coinsLeaderboard.length,
                      ),
                      borderColor: "rgba(255, 255, 255, 0.9)",
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                }}
              />
            </ChartCard>

            <ChartCard title="XP Earned">
              <DoughnutChart
                data={{
                  labels: xpLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "XP Earned",
                      data: xpLeaderboard.map((s) => Number(s.total_xp) || 0),
                      backgroundColor: sliceColors(xpLeaderboard.length),
                      hoverBackgroundColor: sliceHoverColors(
                        xpLeaderboard.length,
                      ),
                      borderColor: "rgba(255, 255, 255, 0.9)",
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                }}
              />
            </ChartCard>

            <ChartCard title="Time Spent (minutes)">
              <BarChart
                data={{
                  labels: timeSpentLeaderboard.map((s) => s.student_name),
                  datasets: [
                    {
                      label: "Time Spent (min)",
                      data: timeSpentLeaderboard.map((s) =>
                        toMinutes ? toMinutes(s.total_time_spent_seconds) : 0,
                      ),
                      backgroundColor: chartPalette.solid.orange,
                      hoverBackgroundColor: chartPalette.hover.orange,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default BatchDashboardView;
